import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CreditCard, Send, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import carteraService from '@/services/carteraService'
import { mensajeDeError } from '@/lib/errores'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import LinkDePago from './LinkDePago'
import { dinero, fecha } from './formato'

/**
 * COBRO de la empresa: enviarle al dueño lo que toque pagar, EN CUALQUIER MOMENTO.
 *
 * Antes la ficha solo cobraba addendas: el primer pago vivía en la cotización y la renovación la
 * mandaba el sistema por correo sin que el asesor la viera. El servidor decide la etapa
 * (`GET /partners/clientes/:subId/cobro`) y aquí se ofrece la acción que le corresponde:
 *   · en prueba          → «Enviar para contratar» (la cotización abierta en «Aceptar y pagar»)
 *   · aceptada sin pagar → el link del primer pago, a la vista y con estado en vivo
 *   · activa / con mora  → el link de la renovación del periodo, aunque falten días
 */
const ETAPA = {
  prueba: { label: 'En prueba', variant: 'secondary' },
  por_pagar: { label: 'Aceptada · falta el pago', variant: 'warning' },
  pagada_sin_entregar: { label: 'Pagada', variant: 'success' },
  al_dia: { label: 'Al día', variant: 'success' },
  mora: { label: 'Con mora', variant: 'danger' },
  automatico: { label: 'Cobro automático', variant: 'success' },
  manual: { label: 'Cobro manual', variant: 'secondary' },
  cancelada: { label: 'Cancelada', variant: 'secondary' },
}

export default function CobroEmpresa({ subscriptionId, onCambio }) {
  const navigate = useNavigate()
  const [cobro, setCobro] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [enviando, setEnviando] = useState(false)
  const [contratoEnviado, setContratoEnviado] = useState(null)
  const alCambiar = useRef(onCambio)
  alCambiar.current = onCambio

  const cargar = useCallback(async () => {
    try {
      setCobro((await carteraService.cobroEmpresa(subscriptionId))?.data || null)
    } catch {
      setCobro(null)
    } finally { setCargando(false) }
  }, [subscriptionId])

  useEffect(() => { cargar() }, [cargar])

  const linkActual = cobro?.enlace?.url
  // El pago entró si la etapa pasó a pagada, o si el link de la renovación dejó de estar pendiente.
  const verificar = useCallback(async () => {
    const d = (await carteraService.cobroEmpresa(subscriptionId, { verificar: true }))?.data || {}
    const pagada = d.pagada || (d.accion === 'renovacion' && (!d.enlace || d.enlace.url !== linkActual))
    return { pagada }
  }, [subscriptionId, linkActual])

  const alPagar = useCallback(() => { cargar(); alCambiar.current?.() }, [cargar])

  const enviar = async () => {
    setEnviando(true)
    try {
      const d = (await carteraService.enviarCobroEmpresa(subscriptionId))?.data || {}
      if (d.estado) setCobro(d.estado)
      if (d.enviado === 'contratar') {
        setContratoEnviado(d.url)
        toast.success('Le enviamos la cotización para que acepte y pague.')
      } else {
        toast.success(d.correoEnviado === false
          ? 'Link listo. No se pudo enviar el correo: cópialo abajo y mándaselo.'
          : 'Link listo y enviado al correo del cliente.')
      }
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo enviar el cobro'), { duration: 10000 })
    } finally { setEnviando(false) }
  }

  if (cargando) {
    return (
      <Card className="p-4 sm:p-5">
        <p className="text-body-sm text-neutral-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Revisando el cobro…</p>
      </Card>
    )
  }
  if (!cobro) return null

  const e = ETAPA[cobro.etapa] || { label: cobro.etapa, variant: 'secondary' }
  const moneda = cobro.moneda || 'USD'
  const propuestaId = cobro.propuesta?.id

  return (
    <Card id="cobro" className="p-5 space-y-3 scroll-mt-24">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-neutral-900 dark:text-neutral-50 flex items-center gap-2"><CreditCard size={16} /> Cobro</h3>
          <p className="text-caption text-neutral-500 dark:text-neutral-400">
            {cobro.mensual ? <>{dinero(cobro.mensual, moneda)}/{cobro.ciclo === 'annual' ? 'año' : 'mes'} + impuestos</> : 'Lo que el dueño tiene pendiente de pagar.'}
            {cobro.proximoCobro && <> · próximo cobro el {fecha(cobro.proximoCobro)}</>}
          </p>
        </div>
        <Badge variant={e.variant}>{e.label}</Badge>
      </div>

      {cobro.etapa === 'mora' && (
        <div role="status" className="rounded-md border border-danger-300 dark:border-danger-700 bg-danger-50 dark:bg-danger-900/20 p-3 flex items-start gap-2 text-body-sm text-danger-700 dark:text-danger-300">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" />
          <span>
            La renovación que venció el {fecha(cobro.proximoCobro)} no se ha pagado y la empresa está bloqueada.
            Al pagar este link se reactiva sola.
          </span>
        </div>
      )}

      {cobro.etapa === 'prueba' && cobro.accion && (
        <p className="text-body-sm text-neutral-600 dark:text-neutral-400">
          {cobro.propuesta?.demoExpiraAt && <>La prueba {new Date(cobro.propuesta.demoExpiraAt) < new Date() ? 'venció' : 'vence'} el {fecha(cobro.propuesta.demoExpiraAt)}. </>}
          Cuando el dueño esté listo, envíale la cotización abierta en <strong>«Aceptar y pagar»</strong>: acepta, paga y sigue en esta misma empresa.
        </p>
      )}

      {contratoEnviado && (
        <p className="text-body-sm text-success-700 dark:text-success-300 flex items-start gap-1.5">
          <CheckCircle2 size={15} className="mt-0.5 shrink-0" />
          <span>Enviada a su correo. También puedes mandarle el enlace por WhatsApp:{' '}
            <a className="underline break-all" href={`https://wa.me/?text=${encodeURIComponent(`Hola, aquí puedes aceptar y pagar tu sistema cd-2b: ${contratoEnviado}`)}`} target="_blank" rel="noopener noreferrer">compartir</a>
          </span>
        </p>
      )}

      {cobro.motivo && !cobro.accion && (
        <p className="text-body-sm text-neutral-600 dark:text-neutral-400">{cobro.motivo}</p>
      )}

      {cobro.enlace ? (
        <LinkDePago
          key={cobro.enlace.url}
          link={cobro.enlace}
          moneda={moneda}
          verificar={verificar}
          onPagada={alPagar}
          textoPagado={cobro.accion === 'renovacion' ? '¡Pago recibido! El periodo quedó renovado.' : '¡Pago recibido! Ya puedes entregar la empresa a producción.'}
          textoMensaje={cobro.accion === 'renovacion' ? 'Hola, este es el link para renovar tu suscripción cd-2b' : 'Hola, este es el link para pagar tu suscripción cd-2b'}
        />
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {cobro.accion === 'enviar_para_contratar' && (
          <Button onClick={enviar} disabled={enviando}>
            <Send size={15} className="mr-1.5" /> {enviando ? 'Enviando…' : contratoEnviado ? 'Reenviar para contratar' : 'Enviar para contratar'}
          </Button>
        )}
        {cobro.accion === 'cobrar_cotizacion' && !cobro.enlace && (
          <Button onClick={enviar} disabled={enviando}>
            <CreditCard size={15} className="mr-1.5" /> {enviando ? 'Generando…' : 'Enviar link de pago'}
          </Button>
        )}
        {cobro.accion === 'renovacion' && (
          <Button onClick={enviar} disabled={enviando} variant={cobro.enlace ? 'outline' : 'default'}>
            <CreditCard size={15} className="mr-1.5" />
            {enviando ? 'Enviando…' : cobro.enlace ? 'Reenviar el link por correo' : `Enviar link de la renovación${cobro.proximoCobro ? ` (vence el ${fecha(cobro.proximoCobro)})` : ''}`}
          </Button>
        )}
        {cobro.accion === 'cobrar_cotizacion' && cobro.enlace && (
          <Button variant="outline" onClick={enviar} disabled={enviando}>
            <Send size={15} className="mr-1.5" /> {enviando ? 'Enviando…' : 'Reenviar el link por correo'}
          </Button>
        )}
        {propuestaId && ['prueba', 'por_pagar'].includes(cobro.etapa) && (
          <Button variant="ghost" onClick={() => navigate(`/consultor/cotizaciones/${propuestaId}?paso=4&seccion=${cobro.etapa === 'por_pagar' ? 'cobro' : 'demo'}`)}>
            Abrir la cotización
          </Button>
        )}
      </div>
    </Card>
  )
}
